import React from 'react';
import Tabs from 'material-ui/Tabs/Tabs';
import Tab from 'material-ui/Tabs/Tab';
import Grid from 'core/grid/domain/grid';
import PageAncestor from 'core/common/page/pageAncestor';
import mdEntityService from 'core/metamodel/mdEntityService'
import productService from 'product/productService';
import {DataObjectDto, EditedGridData, IDomainObject, lovLabel, lovValue, preSave} from 'core/form/formUtils';
import gridService from 'core/grid/gridService';
import GridComp from 'core/grid/component/gridComp';
import Toolmenu from 'core/components/toolmenu/toolmenu';
import {TabTemplate} from 'core/common/config/mui-theme';
import {tabStyle} from 'core/common/config/variables';
import {ErrorComp} from 'core/components/errorComp/errorComp';
import VehicleForm from 'product/component/vehicleForm';
import ProductVehicleForm from 'product/component/productVehicleForm';
import ConfirmationDialog from 'core/components/confirmationDialog/confirmationDialog';
import {MdIconButton, ZzIconButtonMenu, ZzMenuItem} from "core/components/buttons/icons";
import Add from "material-ui/svg-icons/content/add";
import {MODULE, PageProps, PERMISSION, SECURABLE, SECURABLE_TYPE} from "core/metamodel/metaConstant";
import {observer} from "mobx-react";
import {PRICECOMPLEVEL, PriceComponent, ProductVehicle, Vehicle} from "product/productDomain";
import {MovingBlocks} from "core/grid/gridUtils";
import {WithDeleteDialogZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import uiService from "core/common/service/uiService";
import {action, observable} from "mobx";
import {History} from "react-router";
import SvgMoreVert from "material-ui/svg-icons/navigation/more-vert";
import TaskDialog from "core/event/component/taskDialog";
import {ACTIVITYLOCATION, TASKTYPE} from "core/event/eventDomain";
import {formatId} from "core/common/utils/utils";
import ActivityExpando from "core/event/component/activityExpando";
import {sortAndCheckInterval} from "core/common/utils/dateUtils";
import {GridDocument} from "core/document/gridDocument";
import GridDocumentComp from "core/document/component/gridDocumentComp";
import securityService from "core/security/securityService";
import {RESTRICTION} from "core/security/securityDomain";
import {loc} from "../../core/common/config/locales";

const productGridLocation = 'vehicleProductList';
const invoiceGridLocation = 'vehicleInvoiceList';
const invoiceItemGridLocation = 'vehicleInvoiceItemList';
const taskObjectGridLocation = 'taskObjectList';
const activityGridLocation = "activityObjectList";
const vehicleGridDocument = 'vehicleGridDocument';


@observer
export default class VehicleDetail extends PageAncestor<PageProps, undefined> {

  static module = MODULE.product;
  static title = 'Vehicle';
  static icon = 'truck';
  static protectedPage = true;


  static fetchData(routerParams, query) {
    console.log("VehicleDetail#fetchData(%O)", query);


    let metadataPromise = mdEntityService.fetchEntityMetadata(['Vehicle'], ['ProductVehicle', 'PriceComponent', {
      entity: 'Product',
      lov: true
    }, {entity: 'Currency', lov: true}, 'Document']);

    let vehiclePromise: Promise<Vehicle>;
    if (routerParams.id === 'new') {
      let vehicleType = (query.vehicleType) ? parseInt(query.vehicleType) : null;
      let party = (query.partyId) ? {value: query.partyId, label: query.partyLabel} : (securityService.currentUser.hasRestriction(RESTRICTION.YARD_MGMT_HAULER_VEHICLE) ? securityService.restrictionData.employer : null );

      vehiclePromise = Promise.resolve(new Vehicle({$new: true, active: true, vehicleType, party}));
    } else if (routerParams.id === 'copy') {
      vehiclePromise = productService.readVehicle(query.copyId)
        .then(vehicleObject => {
          vehicleObject.cleanOnCopy();
          vehicleObject.active = true;
          return vehicleObject;
        })
    } else {
      vehiclePromise = productService.readVehicle(routerParams.id);
    }

    let gridPromise = gridService.fetchGrids(productGridLocation, invoiceGridLocation, invoiceItemGridLocation, taskObjectGridLocation, activityGridLocation);

    return Promise.all([metadataPromise, vehiclePromise, gridPromise])
      .then(([entityMap, vehicleObject, gridLocationMap]: [any, Vehicle, any]) => {

        vehicleObject.$movingBlocks = new MovingBlocks(productGridLocation, true, query.tab,
          new Grid(gridLocationMap.get(productGridLocation), vehicleObject.vehicleId),
          new Grid(gridLocationMap.get(invoiceGridLocation), vehicleObject.vehicleId),
          new Grid(gridLocationMap.get(invoiceItemGridLocation), vehicleObject.vehicleId),
          new Grid(gridLocationMap.get(taskObjectGridLocation), formatId("Vehicle", vehicleObject.vehicleId)),
          new Grid(gridLocationMap.get(activityGridLocation), formatId("Vehicle", vehicleObject.vehicleId)),
          new GridDocument(vehicleGridDocument, "Docs", {vehicleId: vehicleObject.vehicleId}, false, null, (vehicleObject.$new)?[]:null)
        );

        productService.vehicleObject = vehicleObject.initAfterRead(routerParams.id === 'new');
      });
  }


  private taskDialogRef: TaskDialog;


  /**
   *
   */
  componentWillMount() {
    uiService.addMediaQueryListener(this.mediaQueryListener);
  }

  componentDidUpdate(prevProps: PageProps) {
    if (this.idChanged(prevProps.params.id, this.props.params.id)) {
      productService.vehicleObject.$movingBlocks.searchExposedGrids(productService.vehicleObject);
    }
  }


  componentDidMount() {
    console.debug("%c componentDidMount", "background-color: magenta");
    productService.vehicleObject.$movingBlocks.searchExposedGrids(productService.vehicleObject);
  }

  componentWillUnmount() {
    uiService.removeMediaQueryListener(this.mediaQueryListener);
    productService.vehicleObject = null;
  }


  customValidate = (vehicleObject) => {
    let errors = [];

    let vehicleDto: DataObjectDto<Vehicle> = vehicleObject.createDataObjectDto();
    let editedData: EditedGridData = vehicleDto.gridData[productGridLocation];
    if (editedData) {
      for(let rowObject of editedData.editedRows) {
        let productVehicle: ProductVehicle = rowObject as ProductVehicle;

        const pricesPerCurrency: {[curr: string]: PriceComponent[]} = productVehicle.prices.reduce((acc, price) => {
          let currEntry = acc[lovValue(price.priceCurrency)];
          if (!currEntry) {
            currEntry = [];
            acc[lovValue(price.priceCurrency)] = currEntry;
          }
          currEntry.push(price);
          return acc;
        }, {});

        for(let currency in pricesPerCurrency) {
          let currEntry = pricesPerCurrency[currency];
          if (!sortAndCheckInterval(currEntry)) {
            errors.push({message: "Price validity intervals for " + currency + " on " +  lovLabel(productVehicle.product) + " must not overlap"});
          }
        }


      }
    }

    return errors;
  };


  findRootObject() {
    return productService.vehicleObject;
  }


  /**
   * musim dotahnout prices, jinak spadne customValidate, a nechci menit globalni
   */
  @action onSaveMultiUpdate = () => {
    uiService.showToolbarActionFlag();
    let vehicleDto: DataObjectDto<Vehicle> = productService.vehicleObject.createDataObjectDto();
    let editedData: EditedGridData = vehicleDto.gridData[productGridLocation];
    if (editedData) {
      let promises: Promise<ProductVehicle>[] = editedData.editedRows.map(pv =>  this.onProductVehicleFormLoad(pv as ProductVehicle));
      Promise.all(promises).then((x) => {
        return this.onSave(null);
      });
    } else {
      uiService.hideToolbarActionFlag();
    }
  };


  @action onSave = (evt) => {
    console.log('onSave ');
    uiService.showToolbarActionFlag();


    let result = preSave(productService.vehicleObject, this.customValidate);
    if (!result) {
      return;
    }

    let wasNew: boolean;

    return productService.vehicleSave(productService.vehicleObject.createDataObjectDto())
      .then(action("vehicleSave-callback", (vehicleJson: IDomainObject) => {
        wasNew = productService.vehicleObject.cleanAfterSave(vehicleJson);
        return this.docSavingPromise(productService.vehicleObject.$movingBlocks.getBlock(vehicleGridDocument) as GridDocument, {vehicleId: productService.vehicleObject.vehicleId});
      })).then(x => {
        if (wasNew) {
          this.context.router.replaceWith('vehicleDetail', {id: productService.vehicleObject.vehicleId});
        }
        uiService.hideToolbarActionFlag();
    }).catch(error => {
      console.log("Error %O", error);
      productService.vehicleObject.$errors = error.getMessages();
    });

  };


  @action onDelete = (evt) => {
    console.log('onDelete');
    uiService.showToolbarActionFlag();

    productService.deleteVehicle(productService.vehicleObject.vehicleId)
      .then(response => {
        uiService.hideToolbarActionFlag();
        this.goBackOrHome();
      }, error => {
        productService.vehicleObject.$errors = error.getMessages();
      });
  };


  onAdd = (evt) => {
    console.log('onAdd');
    let party = (productService.vehicleObject.party) ? {
      partyId: productService.vehicleObject.party.value,
      partyLabel: productService.vehicleObject.party.label
    } : null;
    let query = {vehicleType: 1, ...party};
    this.context.router.transitionTo("vehicleDetail", {id: 'new'}, query);
  };

  onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("vehicleDetail", {id: 'copy'}, {copyId: productService.vehicleObject.vehicleId});
  };


  @action addProduct = (evt) => {
    console.log('addProduct %O', evt);
    let grid: Grid = productService.vehicleObject.$movingBlocks.getBlock(productGridLocation) as Grid;
    let rowObject = new ProductVehicle({
      vehicle: productService.vehicleObject.createLov(),
      prices: [],
      $new: true,
      $dirty: true,
      $added: true
    }).initDefaultValues();
    grid.addRow(rowObject);
    let form = rowObject.getForm('ProductVehicleForm');
    form.$open = true;

    productService.vehicleObject.$dirty = true;
  };

  @action onProductVehicleFormLoad = (rowObject: ProductVehicle): Promise<ProductVehicle> => {
    console.log('onProductVehicleFormLoad %O', rowObject);
    if (rowObject.prices) {
      return Promise.resolve(rowObject);
    } else {
      return productService.searchPriceComponents(lovValue(rowObject.product), lovValue(rowObject.vehicle), null, PRICECOMPLEVEL.PRODUCTVEHICLE)
        .then(prices => {
          rowObject.prices = observable.array(prices);
          return rowObject;
        });
    }
  };

  onProductVehicleFormCommit = (rootObject, rowObject) => {
    console.log('onProductVehicleFormCommit rootObject = %O, rowObject = %O', rootObject, rowObject);
    return {partyId: lovValue(rootObject.party)};
  };


  @action onVehicleNotification = (evt) => {
    console.log('onVehicleNotification');
    new Promise((resolve, reject) => {
      this.taskDialogRef.open(resolve, reject, {entity: productService.vehicleObject.$mdEntityRef.id, rowid: [productService.vehicleObject.vehicleId]});
    }).then(() => {
      uiService.hideToolbarActionFlag();
      let movingBlocks = productService.vehicleObject.$movingBlocks;
      let taskGrid: Grid = movingBlocks.getBlock(taskObjectGridLocation) as Grid;
      taskGrid.data = null;
      if (movingBlocks.activeBlockTab === taskObjectGridLocation) {
        this.activateTab(taskGrid);
      }

    }).catch((e) => {
    })

  };


  taskDialogRefHandler = comp => this.taskDialogRef = comp;

  render() {

    let {
      query
    } = this.props;

    let propsForCreateForm = {
      dataObject: productService.vehicleObject,
      rootObject: productService.vehicleObject,
      entity: mdEntityService.getEntity('Vehicle'),
      entities: mdEntityService.entityMap
    };

    console.debug('%c vehicleDetail render query %O', 'background-color: yellow', this.props.query);


    const mainForm = (
      <form style={{marginTop: 10}}>
        <div className="row">
          <div className="col-xs-12">
            <VehicleForm {...propsForCreateForm} />
          </div>
        </div>
      </form>
    );

    const productVehicleEntity = mdEntityService.getEntity('ProductVehicle');
    const rowProductForm = (
      <ProductVehicleForm rootObject={productService.vehicleObject} entity={productVehicleEntity}
                          entities={mdEntityService.entityMap} dataObject={null}/>
    );

    let movingBlocks = productService.vehicleObject.$movingBlocks;

    const gridCompArray = [
      <GridComp grid={movingBlocks.getBlock(productGridLocation) as Grid} key={productGridLocation}
                uiLocation={movingBlocks.getUiLocation(productGridLocation)}
                showEdit={true} showDelete={true} deleteRowIcon="fa fa-trash" rootObject={productService.vehicleObject}
                editForm={rowProductForm} editFormName="ProductVehicleForm" entities={mdEntityService.entityMap}
                onEditFormLoad={this.onProductVehicleFormLoad}
                prepareFormatRowParamsFn={this.onProductVehicleFormCommit}
                multiUpdateSecurable={SECURABLE.productGridUpdate} multiUpdateSaveFn={this.onSaveMultiUpdate}>
        <MdIconButton onToolbar onClick={this.addProduct} disabled={!productService.vehicleObject.party}>
          <Add/>
        </MdIconButton>

      </GridComp>,
      <GridComp grid={movingBlocks.getBlock(invoiceGridLocation) as Grid} key={productGridLocation}
                uiLocation={movingBlocks.getUiLocation(invoiceGridLocation)} entities={mdEntityService.entityMap}/>,
      <GridComp grid={movingBlocks.getBlock(invoiceItemGridLocation) as Grid} key={invoiceItemGridLocation}
                uiLocation={movingBlocks.getUiLocation(invoiceItemGridLocation)} entities={mdEntityService.entityMap}/>,
      <GridComp grid={movingBlocks.getBlock(taskObjectGridLocation) as Grid} key={taskObjectGridLocation}
        uiLocation={movingBlocks.getUiLocation(taskObjectGridLocation)} entities={mdEntityService.entityMap}/>,
      <GridComp grid={movingBlocks.getBlock(activityGridLocation) as Grid} key={activityGridLocation}
    uiLocation={movingBlocks.getUiLocation(activityGridLocation)} entities={mdEntityService.entityMap} showExpand ExpandComponent={ActivityExpando}/>,
      <GridDocumentComp uiLocation={movingBlocks.getUiLocation(vehicleGridDocument)} grid={movingBlocks.getBlock(vehicleGridDocument) as GridDocument} key={vehicleGridDocument}
                                           entities={mdEntityService.entityMap} rootObject={productService.vehicleObject}/>



    ];

    const standAloneGridComp = movingBlocks.pluckStandAloneBlock(gridCompArray);

    const tabArray = gridCompArray.map((gridComp, index) => (
      <Tab label={gridComp.props.grid.label} style={tabStyle}
           onActive={this.activateTab.bind(this, gridComp.props.grid)} key={gridComp.props.grid.getBlockName()}
           value={gridComp.props.grid.getBlockName()}>
        {gridComp}
      </Tab>
    ));

    const tabs = (tabArray.length) ? (
      <Tabs value={movingBlocks.activeBlockTab} className="detail-grid" tabTemplate={TabTemplate}
            inkBarStyle={{transition: 'inherit'}}
            contentContainerStyle={{width: '100%', flexGrow: 1, display: 'flex', minHeight: 0}}
            onChange={this.onTabChange}>
        {tabArray}
      </Tabs>
    ) : '';


    if (uiService.globalMediaQuery.matches) {
      return (
        <main className="main-content" style={{display: 'flex', flexDirection: 'row'}}>
          <div style={{display: 'flex', flexDirection: 'column', height: '100%', width: '50%'}}>
            {this._createToolMenu(productService.vehicleObject, query.gridLocation)}
            <ErrorComp messages={productService.vehicleObject.$errors}/>
            <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
            <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>
            <TaskDialog ref={this.taskDialogRefHandler} entities={mdEntityService.entityMap} location={ACTIVITYLOCATION.Vehicle} taskType={TASKTYPE.VEHICLE_NOTIFICATION}/>
            {mainForm}
            {tabs}
          </div>
          {standAloneGridComp}
        </main>
      );
    } else {
      return (
        <main className="main-content" style={{display: 'flex', flexDirection: 'column'}}>
          {this._createToolMenu(productService.vehicleObject, query.gridLocation)}
          <ErrorComp messages={productService.vehicleObject.$errors}/>
          <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
          <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>
          <TaskDialog ref={this.taskDialogRefHandler} entities={mdEntityService.entityMap} location={ACTIVITYLOCATION.Vehicle} taskType={TASKTYPE.VEHICLE_NOTIFICATION}/>
          {mainForm}
          {tabs}
        </main>
      );
    }
  }


  _createToolMenu(vehicleObject, gridLocation) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} historize
                entityName="Vehicle" entityId={vehicleObject.vehicleId} activeTab={vehicleObject.$movingBlocks.activeBlockTab}>
        <ZzFlatButton onClick={this.onSave} disabled={!vehicleObject.$dirty} label="Save"
                      permission={vehicleObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={vehicleObject.$new} label="New" permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={vehicleObject.$new} label="Copy" permission={PERMISSION.New}/>
        <WithDeleteDialogZzFlatButton entityName="Vehicle" pageAncestor={this} onClick={this.onDelete} disabled={vehicleObject.$new} label="Delete"
                      permission={PERMISSION.Delete}/>
        <ZzIconButtonMenu buttonIcon={<SvgMoreVert/>} securableType={SECURABLE_TYPE.DETAILACTION}>
          <ZzMenuItem primaryText="Create Task" key={4} onClick={this.onVehicleNotification}
                      securable={SECURABLE.vehicleNotification}/>
        </ZzIconButtonMenu>

      </Toolmenu>
    );
  }


}






// WEBPACK FOOTER //
// ./src/product/page/vehicleDetail.tsx